<template>
  <Transition name="auth-notification">
    <div v-if="showNotification" class="auth-notification" :class="notificationType">
      <div class="notification-content">
        <i :class="iconClass"></i>
        <span class="notification-message">{{ message }}</span>
      </div>
      <button v-if="dismissible" @click="dismiss" class="notification-close" aria-label="Fermer">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/authStore'

interface Props {
  dismissible?: boolean
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  dismissible: true,
  duration: 5000
})

const authStore = useAuthStore()
const showNotification = ref(false)
const message = ref('')
const notificationType = ref<'success' | 'warning' | 'error' | 'info'>('info')

// Computed pour l'icône
const iconClass = computed(() => {
  const iconMap = {
    success: 'fas fa-check-circle',
    warning: 'fas fa-exclamation-triangle',
    error: 'fas fa-times-circle',
    info: 'fas fa-info-circle'
  }
  return iconMap[notificationType.value]
})

// Afficher une notification
const show = (msg: string, type: 'success' | 'warning' | 'error' | 'info' = 'info') => {
  message.value = msg
  notificationType.value = type
  showNotification.value = true
  
  // Auto-dismiss après la durée spécifiée
  if (props.dismissible && props.duration > 0) {
    setTimeout(() => {
      dismiss()
    }, props.duration)
  }
}

// Masquer la notification
const dismiss = () => {
  showNotification.value = false
}

// Exposer la méthode show
defineExpose({ show })

// Écouter les changements d'état d'authentification
const handleAuthChange = () => {
  if (authStore.error) {
    show(authStore.error, 'error')
  }
}

// Écouter les changements d'état
onMounted(() => {
  // Surveiller les erreurs d'authentification
  if (authStore.error) {
    handleAuthChange()
  }
})

onUnmounted(() => {
  // Nettoyer les timeouts si nécessaire
})
</script>

<style scoped>
.auth-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 400px;
  min-width: 300px;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.notification-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.notification-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.notification-close:hover {
  opacity: 1;
}

/* Types de notification */
.auth-notification.success {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
}

.auth-notification.warning {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
}

.auth-notification.error {
  background: linear-gradient(135deg, #f44336, #d32f2f);
  color: white;
}

.auth-notification.info {
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: white;
}

/* Icônes */
.auth-notification i {
  font-size: 18px;
  flex-shrink: 0;
}

/* Animations */
.auth-notification-enter-active,
.auth-notification-leave-active {
  transition: all 0.3s ease;
}

.auth-notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.auth-notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Responsive */
@media (max-width: 768px) {
  .auth-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    min-width: auto;
  }
}
</style>
